/**
 * Biocemento Kit Builder — configurateur multi-étapes (style Relentless).
 */
(function () {
  'use strict';

  const STORAGE_TEINTE = 'biocemento:teinte';
  const TOTAL_STEPS = 6;

  const state = {
    step: 1,
    kit: '',
    zone: '',
    m2: 12,
    tier: 'm',
    system: 'standard',
    finish: 'minerale',
    teinte: null,
    catalog: null,
    teintes: [],
    products: {},
  };

  function $(sel, root = document) {
    return root.querySelector(sel);
  }

  function $$(sel, root = document) {
    return [...root.querySelectorAll(sel)];
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(
      cents / 100
    );
  }

  function getBuilder() {
    return document.querySelector('[data-bio-kit-builder]');
  }

  function getConfigEl() {
    return document.getElementById('BioKitBuilderConfig');
  }

  async function loadData() {
    const cfg = getConfigEl();
    const config = cfg ? JSON.parse(cfg.textContent) : {};
    const [catalogRes, teintesRes] = await Promise.all([
      config.catalogUrl ? fetch(config.catalogUrl) : Promise.resolve(null),
      config.teintesUrl ? fetch(config.teintesUrl) : Promise.resolve(null),
    ]);

    if (catalogRes?.ok) state.catalog = await catalogRes.json();
    else if (config.catalog) state.catalog = config.catalog;

    if (teintesRes?.ok) state.teintes = await teintesRes.json();
    else if (config.teintes) state.teintes = config.teintes;

    const handles = Object.values(state.catalog?.kits || {}).map((k) => k.handle);
    await Promise.all(
      handles.map(async (handle) => {
        try {
          const res = await fetch(`${config.routes?.root || '/'}products/${handle}.js`);
          if (res.ok) state.products[handle] = await res.json();
        } catch (e) { /* ignore */ }
      })
    );
  }

  function m2ToTier(m2) {
    if (m2 <= 8) return 's';
    if (m2 <= 18) return 'm';
    return 'l';
  }

  function calcPrice() {
    const kit = state.catalog?.kits?.[state.kit];
    if (!kit) return 0;
    const tier = state.catalog.surfaceTiers[state.tier] || state.catalog.surfaceTiers.m;
    const sysMult = state.catalog.systemMult[state.system] || 1;
    return Math.round(kit.priceFrom * tier.mult * sysMult);
  }

  function surfaceLabel() {
    const t = state.catalog?.surfaceTiers?.[state.tier];
    return t ? `${t.label}, ${t.range}` : '';
  }

  function variantLabels() {
    const tiers = state.catalog?.surfaceTiers || {};
    const fin = state.catalog?.finishes || {};
    const sys = state.catalog?.systems || {};
    return {
      surface: tiers[state.tier]?.label || 'Moyenne',
      finish: fin[state.finish]?.label || state.finish,
      system: sys[state.system]?.label || state.system,
    };
  }

  function findVariant(product) {
    if (!product?.variants) return null;
    const labels = variantLabels();
    return (
      product.variants.find((v) => {
        const o = [v.option1, v.option2, v.option3].filter(Boolean).join(' ').toLowerCase();
        return (
          o.includes(labels.surface.toLowerCase()) &&
          o.includes(labels.finish.toLowerCase()) &&
          o.includes(labels.system.toLowerCase())
        );
      }) ||
      product.variants.find((v) => {
        const o = [v.option1, v.option2, v.option3].filter(Boolean).join(' ').toLowerCase();
        return o.includes(labels.surface.toLowerCase()) && o.includes(labels.finish.toLowerCase());
      }) ||
      product.variants[0]
    );
  }

  function teinteLabel() {
    const t = state.teintes.find((x) => x.slug === state.teinte);
    return t ? `${t.num}, ${t.name}` : state.teinte || '';
  }

  function renderCompare() {
    const el = $('[data-bio-compare-table]');
    if (!el || !state.catalog) return;
    const kit = state.catalog.kits[state.kit];
    if (!kit?.comparison) {
      el.innerHTML = '';
      return;
    }
    const std = kit.comparison.standard;
    const perf = kit.comparison.performance;
    const dot = (n) => '●'.repeat(n) + '○'.repeat(3 - n);
    el.innerHTML = `
      <table class="bio-builder__table">
        <thead><tr><th></th><th>Standard</th><th>Performance</th></tr></thead>
        <tbody>
          <tr><td>Facilité</td><td>${dot(std.ease)}</td><td>${dot(perf.ease)}</td></tr>
          <tr><td>Temps de travail</td><td>${dot(std.time)}</td><td>${dot(perf.time)}</td></tr>
          <tr><td>Étanchéité</td><td>${dot(std.water)}</td><td>${dot(perf.water)}</td></tr>
          <tr><td>Résistance taches</td><td>${dot(std.stain)}</td><td>${dot(perf.stain)}</td></tr>
          <tr><td>Résistance</td><td>${dot(std.strength)}</td><td>${dot(perf.strength)}</td></tr>
        </tbody>
      </table>`;
  }

  function renderTeintesGrid() {
    const grid = $('[data-bio-teintes-grid]');
    if (!grid) return;
    const cfg = getConfigEl() ? JSON.parse(getConfigEl().textContent) : {};
    const base = cfg.assetBase || '';
    grid.innerHTML = state.teintes
      .map(
        (t) => `
      <button type="button" class="bio-builder__teinte${state.teinte === t.slug ? ' is-selected' : ''}"
        data-bio-teinte-pick="${t.slug}" style="--t:${t.hex}" title="${t.name}">
        <span class="bio-builder__teinte-swatch"></span>
        <span class="bio-builder__teinte-num">${t.num}</span>
      </button>`
      )
      .join('');
    $$('[data-bio-teinte-pick]', grid).forEach((btn) => {
      btn.addEventListener('click', () => {
        state.teinte = btn.dataset.bioTeintePick;
        try {
          localStorage.setItem(STORAGE_TEINTE, state.teinte);
        } catch (e) { /* ignore */ }
        renderTeintesGrid();
      });
    });
  }

  function renderZoneChoices() {
    const zoneWrap = $('[data-bio-surface-zone]');
    const zoneGroup = $('[data-bio-choice-group="zone"]');
    if (!zoneWrap || !zoneGroup || !state.catalog) return;
    const kit = state.catalog.kits[state.kit];
    const surfaces = kit?.surfaces || [];
    if (surfaces.length <= 1) {
      zoneWrap.hidden = true;
      state.zone = surfaces[0] || '';
      return;
    }
    zoneWrap.hidden = false;
    zoneGroup.innerHTML = surfaces
      .map(
        (s, i) =>
          `<button type="button" class="bio-builder__choice${i === 0 ? ' is-selected' : ''}" data-value="${s}" data-bio-choice><strong>${s}</strong></button>`
      )
      .join('');
    state.zone = surfaces[0];
    wireChoices(zoneGroup, 'zone');
  }

  function renderSummary() {
    const el = $('[data-bio-summary]');
    if (!el) return;
    const kit = state.catalog?.kits?.[state.kit];
    const t = state.teintes.find((x) => x.slug === state.teinte);
    const labels = variantLabels();
    el.innerHTML = `
      <ul class="bio-builder__summary-list">
        <li><span>Kit</span><strong>${kit?.title || state.kit}</strong></li>
        ${state.zone ? `<li><span>Zones</span><strong>${state.zone}</strong></li>` : ''}
        <li><span>Surface</span><strong>${state.m2} m² (${labels.surface})</strong></li>
        <li><span>Système</span><strong>${labels.system}</strong></li>
        <li><span>Finition</span><strong>${labels.finish}</strong></li>
        <li><span>Teinte</span><strong>${t?.name || '—'}</strong></li>
      </ul>
      ${
        kit?.includes
          ? `<div class="bio-builder__includes"><p>Inclus dans votre kit</p><ul>${kit.includes.map((i) => `<li>${i}</li>`).join('')}</ul></div>`
          : ''
      }`;
    const priceEl = $('[data-bio-total-price]');
    if (priceEl) priceEl.textContent = formatMoney(calcPrice());
  }

  function wireChoices(root, group) {
    $$('[data-bio-choice]', root).forEach((btn) => {
      btn.addEventListener('click', () => {
        $$('[data-bio-choice]', root).forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        state[group] = btn.dataset.value;
        if (group === 'kit') renderZoneChoices();
        if (group === 'system') renderCompare();
      });
    });
  }

  function showStep(n) {
    state.step = n;
    const builder = getBuilder();
    if (!builder) return;
    builder.dataset.step = String(n);
    $$('[data-bio-step]', builder).forEach((sec) => {
      const num = parseInt(sec.dataset.bioStep, 10);
      sec.hidden = num !== n;
      sec.classList.toggle('is-active', num === n);
    });
    $$('[data-bio-step-indicator]', builder).forEach((dot, i) => {
      dot.classList.toggle('is-done', i + 1 < n);
      dot.classList.toggle('is-active', i + 1 === n);
    });
    const back = $('[data-bio-builder-back]', builder);
    const next = $('[data-bio-builder-next]', builder);
    const atc = $('[data-bio-builder-atc]', builder);
    if (back) back.hidden = n <= 1;
    if (next) next.hidden = n >= TOTAL_STEPS;
    if (atc) atc.hidden = n < TOTAL_STEPS;
    if (n === 3) renderCompare();
    if (n === 5) renderTeintesGrid();
    if (n === 6) renderSummary();
  }

  function validateStep() {
    if (state.step === 1 && !state.kit) return 'Choisissez un type de projet.';
    if (state.step === 5 && !state.teinte) return 'Choisissez une teinte.';
    return null;
  }

  async function addToCart() {
    const kit = state.catalog?.kits?.[state.kit];
    if (!kit) return;
    const product = state.products[kit.handle];
    const variant = findVariant(product);
    const status = $('[data-bio-builder-status]');
    const atc = $('[data-bio-builder-atc]');

    if (!variant) {
      if (status) {
        status.hidden = false;
        status.textContent = 'Produit kit à connecter dans Shopify. Lancez le script setup-biocemento-catalog.mjs';
      }
      return;
    }

    atc?.setAttribute('aria-busy', 'true');
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    const props = {
      Teinte: teinteLabel(),
      Surface: `${state.m2} m²`,
      Zone: state.zone || '',
      Projet: kit.title,
    };

    try {
      const formData = new FormData();
      formData.append('id', variant.id);
      formData.append('quantity', '1');
      Object.entries(props).forEach(([k, v]) => {
        if (v) formData.append(`properties[${k}]`, v);
      });

      const cfg = getConfigEl() ? JSON.parse(getConfigEl().textContent) : {};
      const res = await fetch(`${cfg.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.description || json.message || 'Erreur panier');

      const cartRes = await fetch(`${cfg.routes?.root || '/'}cart.js`);
      const cart = await cartRes.json();
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } })
      );
      closeBuilder();
    } catch (err) {
      if (status) {
        status.hidden = false;
        status.textContent = err.message;
      }
    } finally {
      atc?.removeAttribute('aria-busy');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  function openBuilder(preset = '') {
    const builder = getBuilder();
    if (!builder) return;
    if (preset && state.catalog?.kits?.[preset]) {
      state.kit = preset;
      $$('[data-bio-choice-group="kit"] [data-bio-choice]', builder).forEach((btn) => {
        btn.classList.toggle('is-selected', btn.dataset.value === preset);
      });
    }
    const savedTeinte = (() => {
      try {
        return localStorage.getItem(STORAGE_TEINTE);
      } catch (e) {
        return null;
      }
    })();
    if (savedTeinte) state.teinte = savedTeinte;
    builder.hidden = false;
    document.body.classList.add('bio-builder-open');
    showStep(preset ? 2 : 1);
    if (preset) renderZoneChoices();
  }

  function closeBuilder() {
    const builder = getBuilder();
    if (!builder) return;
    builder.hidden = true;
    document.body.classList.remove('bio-builder-open');
  }

  function bindEvents() {
    const builder = getBuilder();
    if (!builder) return;

    wireChoices($('[data-bio-choice-group="kit"]', builder), 'kit');
    wireChoices($('[data-bio-choice-group="system"]', builder), 'system');
    wireChoices($('[data-bio-choice-group="finish"]', builder), 'finish');

    const range = $('[data-bio-m2-range]', builder);
    range?.addEventListener('input', () => {
      state.m2 = parseInt(range.value, 10);
      state.tier = m2ToTier(state.m2);
      const disp = $('[data-bio-m2-display]', builder);
      const tier = $('[data-bio-tier-display]', builder);
      if (disp) disp.textContent = state.m2;
      if (tier) tier.textContent = surfaceLabel();
    });

    $('[data-bio-builder-next]', builder)?.addEventListener('click', () => {
      const err = validateStep();
      const status = $('[data-bio-builder-status]', builder);
      if (err) {
        if (status) {
          status.hidden = false;
          status.textContent = err;
        }
        return;
      }
      if (status) status.hidden = true;
      if (state.step < TOTAL_STEPS) showStep(state.step + 1);
    });

    $('[data-bio-builder-back]', builder)?.addEventListener('click', () => {
      if (state.step > 1) showStep(state.step - 1);
    });

    $('[data-bio-builder-atc]', builder)?.addEventListener('click', addToCart);

    $$('[data-bio-builder-close]', builder).forEach((btn) => {
      btn.addEventListener('click', closeBuilder);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !builder.hidden) closeBuilder();
    });

    document.querySelectorAll('[data-bio-open-builder]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openBuilder(btn.dataset.kitPreset || builder.dataset.kitPreset || '');
      });
    });
  }

  async function init() {
    await loadData();
    const builder = getBuilder();
    if (!builder) return;

    const preset = builder.dataset.kitPreset || new URLSearchParams(location.search).get('kit') || '';
    if (preset && state.catalog?.kits?.[preset]) state.kit = preset;

    const savedTeinte = (() => {
      try {
        return localStorage.getItem(STORAGE_TEINTE);
      } catch (e) {
        return null;
      }
    })();
    const urlTeinte = new URLSearchParams(location.search).get('teinte');
    state.teinte = urlTeinte || savedTeinte || 'lin-naturel';

    bindEvents();

    if (location.hash === '#configurateur' || location.search.includes('open=builder')) {
      openBuilder(preset);
    }

    window.BiocementoKitBuilder = { open: openBuilder, close: closeBuilder, getState: () => ({ ...state }) };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
